import ChildForm from "@/elements/components/manage-children/child-form";
import ChildrenSelectionList from "@/elements/components/manage-children/children-selection-list";
import Loader from "@/elements/components/shared/loader";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { AppBar } from "@/elements/components/ui/app-bar";
import { api } from "@/elements/components/utils/convex-api";
import goBack from "@/elements/components/utils/go-back";
import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import type { Id } from "@teachloop/backend/_generated/dataModel";
import React, { useCallback, useRef, useState } from "react";
import { ScrollView, View, Text } from "react-native";
import { useLocalSearchParams } from "expo-router";

function ManageChildrenScreen() {
  const { id, mode } = useLocalSearchParams();
  const scrollViewRef = useRef<ScrollView>(null);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(
    id as string | null
  );
  const [createMode, setCreateMode] = useState(mode === "create");

  const handleScrollToTop = useCallback(() => {
    scrollViewRef.current?.scrollTo({ y: 0, animated: true });
  }, []);

  return (
    <View className="flex-1 bg-teachLoop-creamBase">
      <AppBar>
        <AppBar.Icon onPress={goBack} />
        <AppBar.Title>Back </AppBar.Title>
      </AppBar>

      <ScrollView ref={scrollViewRef} showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="h-4" />
            <View className="gap-8">
              <View className="gap-1">
                <Text className="font-zilla-slab-medium leading-[1.2] font-medium text-[28px] text-black">
                  Manage children
                </Text>

                <Text className="font-geologica-regular font-normal text-xl text-black leading-[1.5]">
                  {`Manage your children's details here.`}
                </Text>
              </View>

              <ChildrenSelectionList
                selectedChildId={selectedChildId}
                setSelectedChildId={setSelectedChildId}
                createMode={createMode}
                setCreateMode={setCreateMode}
              />

              <ChildFormWrapper
                key={createMode ? "create" : selectedChildId}
                selectedChildId={createMode ? null : selectedChildId}
                createMode={createMode}
                setSelectedChildId={setSelectedChildId}
                setCreateMode={setCreateMode}
                onScrollToTop={handleScrollToTop}
              />
            </View>
          </View>
        </ResponsiveContent>
      </ScrollView>
    </View>
  );
}

function ChildFormWrapper({
  selectedChildId,
  createMode,
  setSelectedChildId,
  setCreateMode,
  onScrollToTop,
}: {
  selectedChildId: string | null;
  createMode: boolean;
  setSelectedChildId: (studentId: Id<"students"> | null) => void;
  setCreateMode: (createMode: boolean) => void;
  onScrollToTop: () => void;
}) {
  const { data: child = null, isSuccess } = useQuery({
    ...convexQuery(api.parent.manageChildren.queries.getChild, {
      id: selectedChildId as Id<"students">,
    }),
    enabled: !!selectedChildId,
  });

  if (!isSuccess && !createMode) {
    return <Loader />;
  }

  return (
    <ChildForm
      child={child ? child : null}
      createMode={createMode}
      setSelectedChildId={setSelectedChildId}
      setCreateMode={setCreateMode}
      onScrollToTop={onScrollToTop}
    />
  );
}

export default ManageChildrenScreen;
